import { useEffect, useRef, useState } from "react";

/**
 * Idle Contemplation — after ~18s without pointer/scroll/key activity, a
 * single short line of wisdom fades in near the bottom, breathes for a few
 * seconds, then fades out. Any activity dismisses it immediately.
 *
 * No pop-ups, no interruptions — text only, respects reduced motion.
 */

const LINES = [
  "Stillness is the frequency beneath sound.",
  "The disciplined heart becomes a temple.",
  "What you attend to, you become.",
  "Presence is the offering.",
  "The current knows the way.",
  "Return to the breath. Return to the field.",
  "Master the mind. Govern the current.",
  "Everything alive is already listening.",
  "Attention shapes reality.",
  "Presence is the first discipline.",
  "The quiet mind hears clearly.",
  "Every frequency begins within.",
];

const IDLE_MS = 18000;

export function IdleQuote() {
  const [visible, setVisible] = useState(false);
  const [line, setLine] = useState(LINES[0]);
  const lastActivity = useRef(0);
  const timer = useRef<number | null>(null);
  const hideTimer = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    lastActivity.current = performance.now();

    const bump = () => {
      lastActivity.current = performance.now();
      if (visible) setVisible(false);
    };

    const check = () => {
      if (visible) return;
      if (performance.now() - lastActivity.current >= IDLE_MS) {
        setLine(LINES[Math.floor(Math.random() * LINES.length)]);
        setVisible(true);
        window.dispatchEvent(new CustomEvent("kaf:idle"));
        if (hideTimer.current) window.clearTimeout(hideTimer.current);
        // Show for ~9s, then softly fade out.
        hideTimer.current = window.setTimeout(() => setVisible(false), reduce ? 6000 : 9000);
      }
    };

    timer.current = window.setInterval(check, 1000);
    const opts: AddEventListenerOptions = { passive: true };
    window.addEventListener("pointermove", bump, opts);
    window.addEventListener("pointerdown", bump, opts);
    window.addEventListener("scroll", bump, opts);
    window.addEventListener("keydown", bump);
    window.addEventListener("touchstart", bump, opts);

    return () => {
      window.removeEventListener("pointermove", bump);
      window.removeEventListener("pointerdown", bump);
      window.removeEventListener("scroll", bump);
      window.removeEventListener("keydown", bump);
      window.removeEventListener("touchstart", bump);
      if (timer.current) window.clearInterval(timer.current);
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
    };
  }, [visible]);

  return (
    <div
      aria-hidden={!visible}
      className={`pointer-events-none fixed inset-x-0 bottom-32 z-30 flex justify-center px-6 transition-opacity duration-[1800ms] ease-out sm:bottom-28 ${
        visible ? "opacity-100" : "opacity-0"
      }`}
    >
      <p className="max-w-xl text-center font-serif text-base italic leading-relaxed text-gold-luminous/85 sm:text-lg">
        {line}
      </p>
    </div>
  );
}
